/**
 * Driver Mobile Hub - Central driver interface with live tracking
 * QR Scanning, GPS Location Sharing, Active Deliveries
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import {
  ArrowLeft,
  Package,
  Truck,
  MapPin,
  Navigation,
  QrCode,
  CheckCircle,
  Clock,
  Phone,
  User,
  Zap,
  ChevronRight,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Locate,
  AlertCircle,
  DollarSign
} from "lucide-react";

const DriverMobileHub = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeDeliveries, setActiveDeliveries] = useState([]);
  const [stats, setStats] = useState({ today: 0, earnings: 0, rating: 0 });
  const [locationSharing, setLocationSharing] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const locationWatchId = useRef(null);
  const locationIntervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [deliveriesRes, statsRes] = await Promise.all([
        api.get("/tracking/driver/active-route").catch(() => ({ data: { deliveries: [] } })),
        api.get("/drivers/me/stats").catch(() => ({ data: { today: 0, earnings: 0, rating: 4.5 } }))
      ]);
      
      setActiveDeliveries(deliveriesRes.data?.deliveries || deliveriesRes.data?.packages || []);
      setStats(statsRes.data || { today: 0, earnings: 0, rating: 4.5 });
    } catch (error) {
      console.error("Failed to fetch driver data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const startLocationSharing = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this device");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setLocationSharing(true);
        toast.success("Location sharing enabled");

        // Start watching position
        locationWatchId.current = navigator.geolocation.watchPosition(
          (pos) => {
            const newLoc = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              heading: pos.coords.heading || 0,
              speed: pos.coords.speed || 0
            };
            setCurrentLocation(newLoc);
          },
          (err) => console.error("Location error:", err),
          { enableHighAccuracy: true, maximumAge: 5000 }
        );

        // Send location to server every 10 seconds
        locationIntervalRef.current = setInterval(async () => {
          if (currentLocation) {
            try {
              await api.post("/tracking/drivers/location", {
                lat: currentLocation.lat,
                lng: currentLocation.lng,
                heading: currentLocation.heading || 0,
                speed: currentLocation.speed || 0
              });
            } catch (e) {
              console.error("Failed to update location:", e);
            }
          }
        }, 10000);
      },
      (error) => {
        toast.error("Failed to get location: " + error.message);
      },
      { enableHighAccuracy: true }
    );
  };

  const stopLocationSharing = () => {
    if (locationWatchId.current) {
      navigator.geolocation.clearWatch(locationWatchId.current);
      locationWatchId.current = null;
    }
    if (locationIntervalRef.current) {
      clearInterval(locationIntervalRef.current);
      locationIntervalRef.current = null;
    }
    setLocationSharing(false);
    toast.info("Location sharing disabled");
  };

  useEffect(() => {
    return () => {
      if (locationWatchId.current) {
        navigator.geolocation.clearWatch(locationWatchId.current);
      }
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
    };
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="driver-mobile-hub">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="p-2 text-slate-400 hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-white">Driver Hub</h1>
              <p className="text-xs text-slate-400">Welcome, {user?.name}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 text-slate-400"
          >
            <RefreshCw className={"h-5 w-5 " + (refreshing ? "animate-spin" : "")} />
          </Button>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {/* Location Sharing Toggle */}
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={"p-2 rounded-lg " + (locationSharing ? "bg-emerald-500/20" : "bg-slate-800")}>
                <Navigation className={"h-5 w-5 " + (locationSharing ? "text-emerald-400" : "text-slate-500")} />
              </div>
              <div>
                <h3 className="font-medium text-white">Live Location</h3>
                <p className="text-xs text-slate-400">
                  {locationSharing ? "Sharing with customers" : "Enable to start deliveries"}
                </p>
              </div>
            </div>
            <button
              onClick={locationSharing ? stopLocationSharing : startLocationSharing}
              className="focus:outline-none"
              data-testid="location-toggle"
            >
              {locationSharing ? (
                <ToggleRight className="h-10 w-10 text-emerald-500" />
              ) : (
                <ToggleLeft className="h-10 w-10 text-slate-500" />
              )}
            </button>
          </div>
          {currentLocation && locationSharing && (
            <div className="mt-3 pt-3 border-t border-slate-800 flex items-center gap-2 text-xs text-slate-400">
              <Locate className="h-3 w-3" />
              <span>{currentLocation.lat.toFixed(5)}, {currentLocation.lng.toFixed(5)}</span>
            </div>
          )}
        </div>

        {/* Today Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-900 rounded-xl p-3 border border-slate-800 text-center">
            <Package className="h-5 w-5 text-emerald-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">{stats.today || 0}</p>
            <p className="text-xs text-slate-400">Deliveries</p>
          </div>
          <div className="bg-slate-900 rounded-xl p-3 border border-slate-800 text-center">
            <DollarSign className="h-5 w-5 text-amber-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">J${stats.earnings?.toLocaleString() || 0}</p>
            <p className="text-xs text-slate-400">Earnings</p>
          </div>
          <div className="bg-slate-900 rounded-xl p-3 border border-slate-800 text-center">
            <Zap className="h-5 w-5 text-purple-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">{stats.rating?.toFixed(1) || "4.5"}</p>
            <p className="text-xs text-slate-400">Rating</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={() => navigate("/driver/scan")}
            className="h-24 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl flex flex-col items-center justify-center gap-2"
            data-testid="scan-btn"
          >
            <QrCode className="h-8 w-8" />
            <span className="font-medium">Scan QR</span>
          </Button>
          <Button
            onClick={() => navigate("/driver/earnings")}
            className="h-24 bg-amber-500 hover:bg-amber-600 text-black rounded-xl flex flex-col items-center justify-center gap-2"
            data-testid="earnings-btn"
          >
            <DollarSign className="h-8 w-8" />
            <span className="font-medium">Earnings</span>
          </Button>
        </div>

        {/* Active Deliveries */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
            <h2 className="font-bold text-white flex items-center gap-2">
              <Truck className="h-5 w-5 text-emerald-400" />
              Active Deliveries ({activeDeliveries.length})
            </h2>
          </div>

          {activeDeliveries.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="h-12 w-12 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-400">No active deliveries</p>
              <p className="text-sm text-slate-500 mt-1">Check queue for new pickups</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {activeDeliveries.map((delivery) => (
                <div
                  key={delivery.id}
                  className="p-4 hover:bg-slate-800/50 cursor-pointer transition-colors"
                  onClick={() => navigate("/driver/delivery/" + delivery.id)}
                  data-testid={"delivery-" + delivery.id}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-white">
                      {delivery.tracking_code || "#" + delivery.id?.slice(0, 8)}
                    </span>
                    <span className={"text-xs px-2 py-1 rounded-full " + (
                      delivery.status === "in_transit" ? "bg-emerald-500/20 text-emerald-400" :
                      delivery.status === "picked_up" ? "bg-blue-500/20 text-blue-400" :
                      "bg-slate-700 text-slate-300"
                    )}>
                      {delivery.status?.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <MapPin className="h-4 w-4" />
                    <span className="truncate">
                      {delivery.destination?.street || delivery.delivery_address?.street || "Destination"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <User className="h-4 w-4" />
                      <span>{delivery.customer_name || "Customer"}</span>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-600" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            onClick={() => navigate("/driver/queue")}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            <Package className="h-4 w-4 mr-2" />
            Pickup Queue
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/driver/history")}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            <Clock className="h-4 w-4 mr-2" />
            History
          </Button>
        </div>
      </main>
    </div>
  );
};

export default DriverMobileHub;
